import { assertNever } from '@kofno/piper';
import { action, computed, makeObservable, observable } from 'mobx';
import { Link } from '../../../../../../Resource/Types';
import { CollaboratorAccessResource, draftFrom, isDraftValid, PermissionType } from '../Types';
import { closed, loadFailed, loading, ready, saved, saveFailed, saving, State } from './Types';

// Every transition switches exhaustively on the current state so the compiler
// flags each action when a new state is added.
class CollaboratorAccessStore {
  @observable
  state: State = closed();

  constructor() {
    makeObservable(this);
  }

  @computed
  get canSave(): boolean {
    switch (this.state.kind) {
      case 'ready':
      case 'save-failed':
        return isDraftValid(this.state.draft);
      case 'closed':
      case 'loading':
      case 'saving':
      case 'saved':
      case 'load-failed':
        return false;
      default:
        return assertNever(this.state);
    }
  }

  @action
  open = (link: Link) => {
    switch (this.state.kind) {
      case 'closed':
        this.state = loading(link);
        break;
      case 'loading':
      case 'ready':
      case 'saving':
      case 'save-failed':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loaded = (resource: CollaboratorAccessResource) => {
    switch (this.state.kind) {
      case 'loading':
        this.state = ready(resource, draftFrom(resource));
        break;
      case 'closed':
      case 'ready':
      case 'saving':
      case 'save-failed':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadFailed = () => {
    switch (this.state.kind) {
      case 'loading':
        this.state = loadFailed();
        break;
      case 'closed':
      case 'ready':
      case 'saving':
      case 'save-failed':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Editing a failed draft clears the error banner by returning to ready.
  @action
  selectPermissionType = (permissionType: PermissionType) => {
    switch (this.state.kind) {
      case 'ready':
      case 'save-failed':
        this.state = ready(this.state.resource, { ...this.state.draft, permissionType });
        break;
      case 'closed':
      case 'loading':
      case 'saving':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  toggleClientGroup = (clientGroupId: number) => {
    switch (this.state.kind) {
      case 'ready':
      case 'save-failed': {
        const { clientGroupIds } = this.state.draft;
        const next = clientGroupIds.includes(clientGroupId)
          ? clientGroupIds.filter((id) => id !== clientGroupId)
          : [...clientGroupIds, clientGroupId];
        this.state = ready(this.state.resource, { ...this.state.draft, clientGroupIds: next });
        break;
      }
      case 'closed':
      case 'loading':
      case 'saving':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  save = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'save-failed':
        if (isDraftValid(this.state.draft)) {
          this.state = saving(this.state.resource, this.state.draft);
        }
        break;
      case 'closed':
      case 'loading':
      case 'saving':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  saved = () => {
    switch (this.state.kind) {
      case 'saving':
        this.state = saved();
        break;
      case 'closed':
      case 'loading':
      case 'ready':
      case 'save-failed':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  saveFailed = () => {
    switch (this.state.kind) {
      case 'saving':
        this.state = saveFailed(this.state.resource, this.state.draft);
        break;
      case 'closed':
      case 'loading':
      case 'ready':
      case 'save-failed':
      case 'saved':
      case 'load-failed':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Closing is valid from every state, so no state inspection is needed.
  @action
  close = () => {
    this.state = closed();
  };
}

export default CollaboratorAccessStore;
